import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Checkbox, App } from 'antd';
import { UserOutlined, LockOutlined, SafetyOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import { setAuth } from '../services/authToken';

const SAVED_ID_KEY = 'sappt_partner_saved_username';
const AUTO_LOGIN_KEY = 'sappt_partner_auto_login';

const LoginPage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saveId, setSaveId] = useState(false);
  const [autoLogin, setAutoLogin] = useState(false);
  const navigate = useNavigate();
  const { message } = App.useApp();

  useEffect(() => {
    if (localStorage.getItem(AUTO_LOGIN_KEY) === '1') setAutoLogin(true);
    const saved = localStorage.getItem(SAVED_ID_KEY);
    if (saved) {
      setSaveId(true);
      form.setFieldsValue({ username: saved });
    }
  }, [form]);

  const onFinish = async (values: { username: string; password: string }) => {
    setLoading(true);
    try {
      const res = await authAPI.partnerLogin(values.username, values.password, autoLogin);
      setAuth(res.data.access_token, res.data.company_name || values.username, autoLogin);
      if (saveId) {
        localStorage.setItem(SAVED_ID_KEY, values.username);
      } else {
        localStorage.removeItem(SAVED_ID_KEY);
      }
      if (autoLogin) {
        localStorage.setItem(AUTO_LOGIN_KEY, '1');
      } else {
        localStorage.removeItem(AUTO_LOGIN_KEY);
      }
      message.success(`${res.data.company_name || values.username}님, 환영합니다.`);
      navigate('/dashboard');
    } catch (err: any) {
      message.error(err.response?.data?.detail || '로그인에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="partner-login-page">
      {/* Left panel - 연구소 파트너 브랜딩 */}
      <div className="partner-login-left">
        <div className="partner-login-left-bg" />

        {/* 잎사귀 + 분자 구조 떠다니는 요소 */}
        <svg className="partner-float-element partner-float-1" width="140" height="140" viewBox="0 0 140 140">
          <path d="M70 10 C95 10, 130 40, 130 70 C130 100, 95 130, 70 130 C45 130, 20 110, 30 80 C40 50, 45 10, 70 10Z"
            fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1.5" />
          <path d="M70 30 L70 120 M50 50 L70 70 M90 45 L70 65 M45 85 L70 95 M95 90 L70 100"
            stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
        </svg>
        <svg className="partner-float-element partner-float-2" width="100" height="100" viewBox="0 0 100 100">
          <polygon points="50,5 95,28 95,72 50,95 5,72 5,28" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="1.5" />
          <polygon points="50,20 78,35 78,65 50,80 22,65 22,35" fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
          <circle cx="50" cy="50" r="4" fill="rgba(251,191,36,0.2)" />
        </svg>
        <svg className="partner-float-element partner-float-3" width="80" height="100" viewBox="0 0 80 100">
          <path d="M40 5 C60 5, 75 25, 75 50 C75 75, 55 95, 40 95 C25 95, 10 80, 15 60 C20 40, 20 5, 40 5Z"
            fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1.2" />
          <line x1="40" y1="20" x2="40" y2="85" stroke="rgba(255,255,255,0.04)" strokeWidth="0.8" />
        </svg>
        <svg className="partner-float-element partner-float-4" width="70" height="61" viewBox="0 0 70 61">
          <polygon points="35,2 68,18 68,46 35,59 2,46 2,18" fill="rgba(255,255,255,0.02)" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
        </svg>

        {/* 분자 네트워크 연결선 */}
        <svg className="partner-mol-network" width="100%" height="100%" viewBox="0 0 500 500" preserveAspectRatio="xMidYMid slice">
          <circle className="partner-mol-dot partner-mol-dot-1" cx="80" cy="100" r="3" fill="rgba(251,191,36,0.35)" />
          <circle className="partner-mol-dot partner-mol-dot-2" cx="200" cy="60" r="4" fill="rgba(251,191,36,0.3)" />
          <circle className="partner-mol-dot partner-mol-dot-3" cx="350" cy="120" r="3.5" fill="rgba(251,191,36,0.25)" />
          <circle className="partner-mol-dot partner-mol-dot-1" cx="420" cy="200" r="3" fill="rgba(251,191,36,0.3)" />
          <circle className="partner-mol-dot partner-mol-dot-2" cx="150" cy="250" r="4.5" fill="rgba(251,191,36,0.35)" />
          <circle className="partner-mol-dot partner-mol-dot-3" cx="300" cy="300" r="3" fill="rgba(251,191,36,0.25)" />
          <circle className="partner-mol-dot partner-mol-dot-1" cx="100" cy="400" r="4" fill="rgba(251,191,36,0.3)" />
          <circle className="partner-mol-dot partner-mol-dot-2" cx="250" cy="420" r="3.5" fill="rgba(251,191,36,0.35)" />
          <circle className="partner-mol-dot partner-mol-dot-3" cx="400" cy="380" r="3" fill="rgba(251,191,36,0.2)" />
          <line className="partner-mol-conn partner-mol-conn-1" x1="80" y1="100" x2="200" y2="60" stroke="rgba(251,191,36,0.08)" strokeWidth="0.8" />
          <line className="partner-mol-conn partner-mol-conn-2" x1="200" y1="60" x2="350" y2="120" stroke="rgba(251,191,36,0.06)" strokeWidth="0.8" />
          <line className="partner-mol-conn partner-mol-conn-3" x1="350" y1="120" x2="420" y2="200" stroke="rgba(251,191,36,0.07)" strokeWidth="0.8" />
          <line className="partner-mol-conn partner-mol-conn-1" x1="80" y1="100" x2="150" y2="250" stroke="rgba(251,191,36,0.06)" strokeWidth="0.8" />
          <line className="partner-mol-conn partner-mol-conn-2" x1="150" y1="250" x2="300" y2="300" stroke="rgba(251,191,36,0.08)" strokeWidth="0.8" />
          <line className="partner-mol-conn partner-mol-conn-3" x1="300" y1="300" x2="400" y2="380" stroke="rgba(251,191,36,0.06)" strokeWidth="0.8" />
          <line className="partner-mol-conn partner-mol-conn-1" x1="150" y1="250" x2="100" y2="400" stroke="rgba(251,191,36,0.07)" strokeWidth="0.8" />
          <line className="partner-mol-conn partner-mol-conn-2" x1="100" y1="400" x2="250" y2="420" stroke="rgba(251,191,36,0.06)" strokeWidth="0.8" />
          <line className="partner-mol-conn partner-mol-conn-3" x1="420" y1="200" x2="300" y2="300" stroke="rgba(251,191,36,0.05)" strokeWidth="0.8" />
        </svg>

        {/* 파티클 */}
        <div className="partner-particle partner-particle-1" />
        <div className="partner-particle partner-particle-2" />
        <div className="partner-particle partner-particle-3" />
        <div className="partner-particle partner-particle-4" />

        <div className="partner-login-left-content">
          <div className="partner-login-badge">
            <span className="partner-login-badge-dot" />
            PARTNER PORTAL
          </div>

          <div className="partner-login-brand">
            <h1 className="partner-login-logo">SAPPT</h1>
            <div className="partner-login-logo-divider" />
            <p className="partner-login-subtitle">Safety Assessment of Pesticide<br />Protocol & Testing</p>
          </div>

          <h2 className="partner-login-headline">
            시험 진행 현황을<br />
            <span className="partner-login-headline-accent">실시간</span>으로 확인하세요
          </h2>
          <p className="partner-login-desc">
            의뢰하신 농약 시험의 진행 상황, 처리 일정, 조사 결과를<br />
            파트너 전용 포털에서 편리하게 조회할 수 있습니다.
          </p>

          <div className="partner-login-features">
            {[
              { icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                  <circle cx="12" cy="16" r="2" fill="rgba(251,191,36,0.3)" />
                </svg>
              ), label: '시험 일정', desc: '처리/조사 일정 확인' },
              { icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                </svg>
              ), label: '진행 현황', desc: '실시간 진행 상태' },
              { icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
                  <line x1="9" y1="15" x2="15" y2="15" /><line x1="9" y1="11" x2="15" y2="11" />
                </svg>
              ), label: '결과 조회', desc: '성적서 확인' },
              { icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <polyline points="9 12 11 14 15 10" />
                </svg>
              ), label: '보안 접속', desc: 'SSL 암호화 통신' },
            ].map((item, i) => (
              <div key={i} className="partner-login-feature-card">
                <div className="partner-login-feature-icon">{item.icon}</div>
                <div className="partner-login-feature-label">{item.label}</div>
                <div className="partner-login-feature-desc">{item.desc}</div>
              </div>
            ))}
          </div>

          {/* SAPPT Domains */}
          <div className="partner-login-domains">
            <div className="partner-login-domains-title">SAPPT Domains</div>
            <div className="partner-login-domains-grid">
              {[
                { label: 'Admin', domain: 'lab.sappt.co.kr', desc: '관리자 시스템' },
                { label: 'Partner', domain: 'partner.sappt.co.kr', desc: '파트너 포털', active: true },
              ].map((item, i) => (
                <div key={i} className={`partner-login-domain-item${item.active ? ' active' : ''}`}>
                  <span className="partner-login-domain-label">{item.label}</span>
                  <span className="partner-login-domain-url">{item.domain}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="partner-login-footer-text">
            <span>SAPPT v2.1</span>
            <span className="partner-login-footer-sep" />
            <span>partner.sappt.co.kr</span>
          </div>
        </div>
      </div>

      {/* Right panel - Login form */}
      <div className="partner-login-right">
        <div className="partner-login-form-wrapper">
          <div className="partner-login-form-card">
            <div className="partner-login-form-header">
              <div className="partner-login-form-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 3h6v8l4 7H5l4-7V3z" />
                  <line x1="9" y1="3" x2="15" y2="3" />
                  <line x1="10" y1="9" x2="14" y2="9" />
                  <circle cx="12" cy="15" r="1.5" fill="rgba(217,119,6,0.3)" />
                </svg>
              </div>
              <h2 className="partner-login-form-title">Welcome Back</h2>
              <p className="partner-login-form-subtitle">파트너 계정으로 로그인하세요</p>
            </div>

            <Form
              form={form}
              onFinish={onFinish}
              layout="vertical"
              className="partner-login-form"
              size="large"
            >
              <Form.Item
                name="username"
                label={<span className="partner-login-label">아이디</span>}
                rules={[{ required: true, message: '아이디를 입력하세요' }]}
              >
                <Input
                  prefix={<UserOutlined style={{ color: '#d97706' }} />}
                  placeholder="아이디를 입력하세요"
                  className="partner-login-input"
                />
              </Form.Item>
              <Form.Item
                name="password"
                label={<span className="partner-login-label">비밀번호</span>}
                rules={[{ required: true, message: '비밀번호를 입력하세요' }]}
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: '#d97706' }} />}
                  placeholder="비밀번호를 입력하세요"
                  className="partner-login-input"
                />
              </Form.Item>
              <Form.Item style={{ marginBottom: 8, marginTop: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                  <Checkbox
                    checked={saveId}
                    onChange={e => setSaveId(e.target.checked)}
                    style={{ fontSize: 13, color: '#92400e' }}
                  >
                    아이디 저장
                  </Checkbox>
                  <Checkbox
                    checked={autoLogin}
                    onChange={e => setAutoLogin(e.target.checked)}
                    style={{ fontSize: 13, color: '#92400e' }}
                  >
                    자동로그인
                  </Checkbox>
                </div>
              </Form.Item>
              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  loading={loading}
                  className="partner-login-btn"
                >
                  로그인
                </Button>
              </Form.Item>
            </Form>

            <div className="partner-login-security">
              <SafetyOutlined />
              <span>SSL 암호화 통신으로 안전하게 보호됩니다</span>
            </div>
          </div>

          <div className="partner-login-help">
            계정 발급 문의는 시험 담당자에게 연락하세요
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
